import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LegalLayout from './LegalLayout';
import { usePrerenderSignal } from '../../utils/usePrerenderSignal';
import './LegalPage.css';

const PrivacyPage: React.FC = () => {
  usePrerenderSignal();
  return (
  <>
    <Helmet>
      <title>Privacy Policy - PRTCL</title>
      <meta name="description" content="PRTCL's Privacy Policy. What we collect, how we use it, how we protect it. Operated by Align to Grind LLC." />
      <link rel="canonical" href="https://www.prtclapp.com/privacy" />
      <meta property="og:title" content="Privacy Policy - PRTCL" />
      <meta property="og:url" content="https://www.prtclapp.com/privacy" />
      <meta name="robots" content="noindex,follow" />
    </Helmet>
    <LegalLayout title="Privacy Policy" eyebrow="Privacy" updated="Last updated: July 14, 2026">
    <div className="legal-content">
      <h2>1. Overview</h2>
      <p>PRTCL is owned and operated by Align to Grind LLC ("PRTCL," "we," "us," or "our"), based in Nevada, United States.</p>
      <p>This Privacy Policy explains what information we collect, how we use it, and how we protect it when you use the PRTCL mobile application (the "App").</p>
      <p>PRTCL is a personal tracking, educational, and organizational tool for informational and record-keeping purposes only. We do not provide medical services, medical advice, or dosing recommendations.</p>
      <p>By creating an account or using the App, you agree to this Privacy Policy. If you do not agree, please do not use the App.</p>

      <h2>2. Information We Collect</h2>

      <h3>Account Information</h3>
      <p>When you create an account, we collect:</p>
      <ul>
        <li>Email address</li>
        <li>Password (stored in hashed form; we never store plaintext passwords)</li>
        <li>Profile information you choose to provide (such as display name)</li>
      </ul>
      <p>You can also create your account using Sign in with Apple or Sign in with Google instead of a password. When you do, that provider confirms your identity and may share your name and an email address with us to set up your account. Sign in with Apple offers a private relay option that hides your real email address.</p>

      <h3>Onboarding Information</h3>
      <p>If you choose to provide information during onboarding, we may collect:</p>
      <ul>
        <li>Goals and interests you select (such as "Track peptides," "Learn about peptides," "Set health goals")</li>
        <li>Experience level with peptides</li>
        <li>Primary wellness goals</li>
        <li>Information complexity preference (beginner, intermediate, or advanced)</li>
      </ul>
      <p>This information is collected solely to personalize your app experience and is stored in your account.</p>

      <h3>User-Entered Data</h3>
      <p>Information you choose to enter in the App, including:</p>
      <ul>
        <li>Peptide names and details</li>
        <li>Inventory and supply information</li>
        <li>Dose log entries and history</li>
        <li>Protocol and schedule data</li>
        <li>Cycle tracking data</li>
        <li>Reminder preferences</li>
        <li>Notes and personal annotations</li>
      </ul>

      <h3>Tracking Data</h3>
      <p>If you choose to use the App’s tracking features, we may collect:</p>
      <ul>
        <li>Injection site selections and history</li>
      </ul>
      <p>This tracking data is collected to provide app functionality and is stored in your account.</p>

      <h3>Dose Calculator Data</h3>
      <p>If you use the Dose Calculator, the values you enter (such as syringe type, vial quantity, reconstitution volume, desired dose) are processed locally to generate calculation results. Dose Calculator inputs and outputs are not stored in your account or transmitted to any third party unless you choose to save the result as a log entry.</p>

      <h3>Subscription and Payment Information</h3>
      <p>If you purchase a paid subscription, we collect information necessary to manage your subscription:</p>
      <ul>
        <li><strong>iOS purchases:</strong> A RevenueCat-issued anonymous identifier, your subscription tier, renewal status, and purchase history. PRTCL does not receive your payment card information; Apple processes all payments on iOS.</li>
        <li><strong>Web purchases:</strong> Billing email and a Stripe customer identifier. Stripe processes your payment card information directly and PRTCL never receives or stores your full card number.</li>
      </ul>

      <h3>Search Queries (AI-Assisted Features)</h3>
      <p>When you use semantic search in the App, your search query text is sent to Google's Generative Language API (Gemini) to generate an embedding used to find matching content. When you request an AI-generated summary, the relevant text is sent to Anthropic's Claude API. Your account email and identity are not included in these requests. Query text is processed by the AI provider in accordance with the provider's API policy and is not retained by PRTCL beyond a short-lived performance cache.</p>

      <h3>Device and Technical Information</h3>
      <p>We collect limited technical information to maintain app functionality and troubleshoot issues:</p>
      <ul>
        <li>Device type and operating system version</li>
        <li>App version</li>
        <li>Crash logs and error reports collected via Sentry. We have configured Sentry to scrub personally identifiable information and health data from error reports before they leave your device. Sentry receives only sanitized error context.</li>
      </ul>

      <h3>Usage and Product Analytics</h3>
      <p>To understand how the App is used and to improve it, we log first-party product-usage events, such as which screens you open, which features you use, and when you complete key steps (for example, finishing the guided walkthrough or viewing a lesson). When you are signed in, these events are associated with your account. This is first-party analytics used only to operate and improve PRTCL. It is not third-party behavioral tracking, which we do not use (see Section 8).</p>

      <h2>3. How We Use Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide and operate the App</li>
        <li>Store and sync your data across devices</li>
        <li>Authenticate your account</li>
        <li>Provide app features (peptide library, dose logs, protocols, schedules, reminders, injection site tracking, dose calculator, and educational content)</li>
        <li>Send notifications about scheduled doses and other reminders you configure</li>
        <li>Manage access to the App</li>
        <li>Personalize your experience based on onboarding preferences and selected information complexity level</li>
        <li>Respond to support requests</li>
        <li>Maintain security and prevent misuse</li>
      </ul>
      <p><strong>We do not:</strong></p>
      <ul>
        <li>Provide medical advice, diagnosis, or treatment</li>
        <li>Use your data to make dosing or health recommendations</li>
        <li>Sell your personal information</li>
        <li>Use your tracking data or calculator data for advertising</li>
        <li>Share your data with third parties for marketing purposes</li>
        <li>Store or transmit Dose Calculator inputs or outputs to any third party</li>
      </ul>

      <h2>4. Data Storage and Security</h2>
      <p>PRTCL uses the following infrastructure to store and process your data:</p>
      <ul>
        <li><strong>Supabase</strong> - authentication and primary database hosting</li>
        <li><strong>Vercel</strong> - web hosting for the App and the marketing site</li>
        <li>Industry-standard encryption in transit (TLS/SSL) and at rest</li>
      </ul>
      <p>Data is encrypted in transit and at rest where supported by our providers. We use industry-standard security measures, including hashed passwords and secure database access controls. Despite these measures, no system can guarantee absolute security. You use the App at your own risk.</p>
      <p><strong>Tracking Data Storage:</strong></p>
      <p>Injection site and other tracking data is stored securely in our database within your user account. This data is accessible only to you.</p>

      <h2>5. Local Device Storage</h2>
      <p>To support app functionality, PRTCL may store limited data locally on your device, including:</p>
      <ul>
        <li>Cached user profile and subscription state (for faster loading and offline access)</li>
        <li>Notification preferences and scheduled notification identifiers</li>
        <li>Onboarding completion status</li>
        <li>Information complexity level preference</li>
        <li>Authentication tokens</li>
        <li>A short-lived cache of search-query embeddings to improve performance</li>
      </ul>

      <h2>6. Data Sharing</h2>
      <p>We do not sell, rent, or trade your personal data. We do not share your data with advertisers or marketing networks.</p>
      <p>We share information only with service providers necessary to operate the App, and only the minimum data needed for that service to function. Each service is contractually bound to use your data only to provide services to PRTCL.</p>

      <h2>7. Third-Party Services</h2>
      <p>PRTCL relies on the following third-party services. Each operates under its own privacy policy.</p>

      <h3>Infrastructure</h3>
      <ul>
        <li><strong>Supabase</strong> - authentication, database, and edge function hosting. <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">supabase.com/privacy</a></li>
        <li><strong>Vercel</strong> - web hosting and request logging. <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">vercel.com/legal/privacy-policy</a></li>
      </ul>

      <h3>Identity Providers</h3>
      <ul>
        <li><strong>Apple (Sign in with Apple)</strong> - confirms your identity when you choose Apple sign-in and may provide your name and an email address, which can be a private relay address. <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">apple.com/legal/privacy</a></li>
        <li><strong>Google (Sign in with Google)</strong> - confirms your identity when you choose Google sign-in and may provide your name and email address. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a></li>
      </ul>

      <h3>Payments and Subscriptions</h3>
      <ul>
        <li><strong>Apple</strong> - processes all in-app purchases on iOS through the App Store. <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">apple.com/legal/privacy</a></li>
        <li><strong>RevenueCat</strong> - manages iOS subscription entitlements. RevenueCat receives an anonymous identifier and your subscription status. <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer">revenuecat.com/privacy</a></li>
        <li><strong>Stripe</strong> - processes payment for web subscriptions. <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a></li>
      </ul>

      <h3>AI-Assisted Features</h3>
      <ul>
        <li><strong>Google (Generative Language API / Gemini)</strong> - generates embeddings for your search queries to enable semantic search. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a></li>
        <li><strong>Anthropic (Claude API)</strong> - generates summaries when you request an AI-powered summary of a video or protocol. <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">anthropic.com/legal/privacy</a></li>
        <li><strong>Supadata.ai</strong> - fetches video transcripts when summarizing third-party video content. <a href="https://supadata.ai/privacy" target="_blank" rel="noopener noreferrer">supadata.ai/privacy</a></li>
      </ul>

      <h3>Diagnostics</h3>
      <ul>
        <li><strong>Sentry</strong> - collects sanitized crash logs and error reports. We have configured Sentry to scrub personally identifiable information and health data before transmission. <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer">sentry.io/privacy</a></li>
      </ul>

      <h2>8. Tracking and Analytics</h2>
      <p>PRTCL does not track you across other companies' apps or websites, and does not share your data with data brokers.</p>
      <p>We do not use behavioral analytics SDKs (such as Google Analytics, Mixpanel, Segment, Amplitude, or Meta Pixel), advertising SDKs, or cross-site tracking technologies.</p>
      <p>We do log first-party product-usage events (for example, which screens or features you use) that are tied to your account when you are signed in. We use this only to operate and improve the App. This is separate from the third-party behavioral tracking described above, which we do not use.</p>
      <p><strong>App Tracking Transparency (iOS):</strong> PRTCL does not request the App Tracking Transparency (ATT) permission because we do not track you across apps or websites owned by other companies, nor share your data with data brokers.</p>
      <p><strong>Do Not Track and Global Privacy Control:</strong> The App and our website do not respond differently to "Do Not Track" browser signals because no industry standard for responding to such signals has been established. We honor Global Privacy Control (GPC) signals where applicable as an opt-out request under the California Consumer Privacy Act.</p>

      <h2>9. Data Retention</h2>
      <p>Your account data, user-entered data, and tracking data is stored as long as your account remains active.</p>
      <p>If you delete your account or request deletion:</p>
      <ul>
        <li>Your personal, tracking, and user-entered data will be permanently removed within 30 days</li>
        <li>Some limited information may be retained if required for legal, security, or fraud-prevention purposes</li>
      </ul>
      <h2>10. Your Privacy Rights</h2>
      <p>You may:</p>
      <ul>
        <li>Access and update your information within the App</li>
        <li>Modify or delete tracking data (injection sites, dose logs, etc.) at any time</li>
        <li>Disable notifications through device settings</li>
        <li>Request account and data deletion by contacting support</li>
        <li>Request a copy of the data we hold about you</li>
      </ul>
      <p><strong>To request deletion or ask about your data:</strong></p>
      <p>Email: support@prtclapp.com</p>
      <p>We will respond to data requests within 30 days.</p>

      <h3>California Residents (CCPA/CPRA)</h3>
      <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA), including:</p>
      <ul>
        <li><strong>Right to Know:</strong> You may request that we disclose the categories and specific pieces of personal information we have collected about you, the categories of sources from which we collected it, the business purpose for collecting it, and the categories of third parties with whom we share it.</li>
        <li><strong>Right to Delete:</strong> You may request that we delete personal information we have collected from you, subject to certain exceptions.</li>
        <li><strong>Right to Opt-Out of Sale or Sharing:</strong> PRTCL does not sell or share personal information as those terms are defined under the CCPA/CPRA.</li>
        <li><strong>Right to Limit Use of Sensitive Personal Information:</strong> You may request that we limit our use of sensitive personal information, such as health-related data you enter, to what is necessary to provide the App. PRTCL already uses this information only to deliver the features you request.</li>
        <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA/CPRA rights.</li>
      </ul>
      <p>To exercise these rights, contact us at support@prtclapp.com. We will verify your identity before fulfilling your request and respond within 45 days.</p>

      <h3>Washington Residents (My Health My Data Act)</h3>
      <p>If you are a Washington State resident, your consumer health data is governed by our separate <Link to="/consumer-health-data">Consumer Health Data Privacy Policy</Link>, which describes the categories of consumer health data we collect, the sources and purposes of that collection, the third parties with whom we share it, and how to exercise your rights under the Washington My Health My Data Act (RCW 19.373).</p>
      <p>In summary: PRTCL does not sell consumer health data, does not use it for advertising, and only collects it as necessary to provide the tracking, logging, and organizational features you actively request through the App. To exercise your rights, contact <a href="mailto:support@prtclapp.com">support@prtclapp.com</a>.</p>

      <h3>Nevada Residents (Consumer Health Data Privacy Law)</h3>
      <p>If you are a Nevada resident, you have rights under Nevada’s Consumer Health Data Privacy Law, including:</p>
      <ul>
        <li><strong>Right to Confirmation:</strong> You may request confirmation of whether we collect, share, or sell your consumer health data.</li>
        <li><strong>Right to a List of Third Parties:</strong> You may request a list of third parties with whom we have shared your consumer health data.</li>
        <li><strong>Right to Opt-Out:</strong> You may request that we stop collecting, sharing, or selling your consumer health data.</li>
        <li><strong>Right to Deletion:</strong> You may request deletion of your consumer health data.</li>
      </ul>
      <p>To exercise these rights, contact support@prtclapp.com. If we deny a request, you may appeal by emailing us. If you are unsatisfied with the result, you may contact the Nevada Attorney General.</p>
      <p>PRTCL does not sell consumer health data.</p>

      <h2>11. Age Requirement</h2>
      <p>PRTCL is intended for users 18 years or older.</p>
      <p>We do not knowingly collect information from anyone under 18. If we learn that a person under 18 has created an account, we will delete the account and associated information promptly.</p>

      <h2>12. Children’s Privacy</h2>
      <p>PRTCL does not knowingly collect or solicit personal information from anyone under 18 years of age. If we learn we have collected personal information from a child under 18, we will delete that information as quickly as possible. If you believe a child under 18 may have provided us with personal information, please contact us at support@prtclapp.com.</p>
      <p>PRTCL is not directed at children under 13. In compliance with the Children’s Online Privacy Protection Act (COPPA), we do not knowingly collect personal information from children under 13.</p>

      <h2>13. Health Information Disclaimer</h2>
      <p>Information entered into or synced with PRTCL may relate to health, wellness, or research substances. PRTCL:</p>
      <ul>
        <li>Does not provide medical advice or dosing recommendations</li>
        <li>Does not diagnose, treat, cure, or prevent any disease or medical condition</li>
        <li>Does not provide nutritional, dietary, or hydration advice</li>
        <li>Does not recommend specific substances, dosages, or protocols</li>
        <li>Stores information only as entered by the user</li>
        <li>Does not verify or validate any user-entered data, including calculator inputs or other tracking data</li>
      </ul>
      <p>Users are responsible for how they use the information. Always consult a qualified healthcare professional for medical decisions.</p>

      <h2>14. International Users</h2>
      <p>PRTCL is operated from the United States.</p>
      <p>If you access the App from outside the United States, your information may be transferred to and processed in the United States, where data protection laws may differ from those in your jurisdiction.</p>
      <p>By using the App, you consent to this transfer and processing.</p>

      <h2>15. Data Security Measures</h2>
      <p>We implement reasonable administrative, technical, and physical safeguards to protect your data, including:</p>
      <ul>
        <li>Encryption of data in transit using TLS/SSL</li>
        <li>Encryption of data at rest</li>
        <li>Hashed password storage</li>
        <li>Role-based access controls</li>
        <li>Regular security reviews</li>
      </ul>
      <p>These measures meet or exceed the reasonable standard of care within the industry. However, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.</p>

      <h2>16. Changes to This Policy</h2>
      <p>We may update this Privacy Policy from time to time.</p>
      <p>The updated version will be available within the App and at our website with a revised "Last updated" date. Continued use of the App after changes constitutes acceptance of the updated policy. For material changes, we will provide notice through the App or via email.</p>

      <div className="legal-contact">
        <h2>17. Contact</h2>
        <p>If you have questions about this Privacy Policy or your data:</p>
        <p>Align to Grind LLC<br />Email: <a href="mailto:support@prtclapp.com">support@prtclapp.com</a></p>
      </div>
    </div>
    </LegalLayout>
  </>
  );
};

export default PrivacyPage;
